// Tiny IndexedDB wrapper for offline gallery cache.
// Stores the latest /api/plants payload and a per-scan record so detail pages can
// still open while offline.
import { openDB } from "idb";

const DB = "prairie-offline-v1";
const STORE_LIST = "plants_list";      // key: user_id   value: { items, savedAt }
const STORE_SCAN = "plants_scan";      // key: scan_id   value: full scan doc
const STORE_QUEUE = "identify_queue";  // key: scan_id   value: { scan_id, user_id, added_at, retries, last_error, next_attempt_at }

let _dbPromise = null;
const getDB = () => {
  if (!_dbPromise) {
    _dbPromise = openDB(DB, 2, {
      upgrade(d, oldVersion) {
        if (!d.objectStoreNames.contains(STORE_LIST)) d.createObjectStore(STORE_LIST);
        if (!d.objectStoreNames.contains(STORE_SCAN)) d.createObjectStore(STORE_SCAN);
        if (oldVersion < 2 && !d.objectStoreNames.contains(STORE_QUEUE)) {
          d.createObjectStore(STORE_QUEUE);
        }
      },
    });
  }
  return _dbPromise;
};

export const saveCachedPlants = async (userId, items) => {
  if (!userId || !Array.isArray(items)) return;
  try {
    const d = await getDB();
    const tx = d.transaction([STORE_LIST, STORE_SCAN], "readwrite");
    await tx.objectStore(STORE_LIST).put({ items, savedAt: Date.now() }, userId);
    const scanStore = tx.objectStore(STORE_SCAN);
    for (const it of items) {
      if (it?.scan_id) await scanStore.put(it, it.scan_id);
    }
    await tx.done;
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[offlineCache] saveCachedPlants failed:", e); }
};

export const getCachedPlants = async (userId) => {
  if (!userId) return null;
  try {
    const d = await getDB();
    return (await d.get(STORE_LIST, userId)) || null;
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[offlineCache] getCachedPlants failed:", e); return null; }
};

export const getCachedScan = async (scanId) => {
  if (!scanId) return null;
  try {
    const d = await getDB();
    return (await d.get(STORE_SCAN, scanId)) || null;
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[offlineCache] getCachedScan failed:", e); return null; }
};

export const clearOfflineCache = async () => {
  try {
    const d = await getDB();
    const tx = d.transaction([STORE_LIST, STORE_SCAN, STORE_QUEUE], "readwrite");
    await tx.objectStore(STORE_LIST).clear();
    await tx.objectStore(STORE_SCAN).clear();
    await tx.objectStore(STORE_QUEUE).clear();
    await tx.done;
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[offlineCache] clearOfflineCache failed:", e); }
};

// ---- Identify queue helpers (pause & resume) ----------------------------- //

export const enqueueIdentifyRetry = async (scanId, userId) => {
  if (!scanId || !userId) return;
  try {
    const d = await getDB();
    const existing = await d.get(STORE_QUEUE, scanId);
    const entry = {
      scan_id: scanId,
      user_id: userId,
      added_at: existing?.added_at || Date.now(),
      retries: existing?.retries || 0,
      last_error: null,
      next_attempt_at: Date.now(),   // eligible immediately
    };
    await d.put(STORE_QUEUE, entry, scanId);
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[queue] enqueue failed:", e); }
};

export const dequeueIdentifyRetry = async (scanId) => {
  if (!scanId) return;
  try {
    const d = await getDB();
    await d.delete(STORE_QUEUE, scanId);
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[queue] dequeue failed:", e); }
};

export const listIdentifyQueue = async (userId) => {
  try {
    const d = await getDB();
    const all = await d.getAll(STORE_QUEUE);
    return userId ? all.filter((e) => e.user_id === userId) : all;
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("[queue] list failed:", e);
    return [];
  }
};

export const bumpIdentifyRetry = async (scanId, errorMessage, backoffMs) => {
  if (!scanId) return;
  try {
    const d = await getDB();
    const cur = await d.get(STORE_QUEUE, scanId);
    if (!cur) return;
    cur.retries = (cur.retries || 0) + 1;
    cur.last_error = errorMessage || null;
    cur.next_attempt_at = Date.now() + (backoffMs || 30_000);
    await d.put(STORE_QUEUE, cur, scanId);
  } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[queue] bump failed:", e); }
};
